<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import IconBox from '@uiKit/components/IconBox.vue'
import { ONE_MINUTE_MS, formatTimestamp } from '@shared/utils'
import type { PiIconName } from '@uiKit/elements/PiIcon.vue'
import type { Notification, NotificationType } from '../types/notificationTypes'

interface Props {
  notification: Notification
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'click', notification: Notification): void
}>()

interface TypeStyle {
  bg: string
  iconColor: string
  icon: PiIconName
}

const typeStyle: Record<NotificationType, TypeStyle> = {
  investment: {
    bg: 'var(--p-primary-50)',
    iconColor: 'var(--p-primary-600)',
    icon: 'chart-bar',
  },
  closing_soon: {
    bg: 'var(--p-warning-50)',
    iconColor: 'var(--p-warning-600)',
    icon: 'clock',
  },
  rental_income: {
    bg: 'var(--p-success-50)',
    iconColor: 'var(--p-success-600)',
    icon: 'arrow-circle-up',
  },
  wallet: {
    bg: 'var(--p-success-50)',
    iconColor: 'var(--p-success-600)',
    icon: 'wallet',
  },
  refund: {
    bg: 'var(--p-success-50)',
    iconColor: 'var(--p-success-600)',
    icon: 'arrow-circle-down',
  },
  kyc: {
    bg: 'var(--p-info-50)',
    iconColor: 'var(--p-info-600)',
    icon: 'verified',
  },
  bank_account: {
    bg: 'var(--p-info-50)',
    iconColor: 'var(--p-info-600)',
    icon: 'building-columns',
  },
  property: {
    bg: 'var(--p-primary-50)',
    iconColor: 'var(--p-primary-600)',
    icon: 'building',
  },
  tokens: {
    bg: 'var(--p-success-50)',
    iconColor: 'var(--p-success-600)',
    icon: 'percentage',
  },
  general: {
    bg: 'var(--p-surface-100)',
    iconColor: 'var(--p-text-muted-color)',
    icon: 'bell',
  },
  failed: {
    bg: 'var(--p-danger-50)',
    iconColor: 'var(--p-danger-600)',
    icon: 'exclamation-circle',
  },
}

const MINUTES_PER_HOUR = 60
const HOURS_PER_DAY = 24

const relativeTime = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / ONE_MINUTE_MS)
  if (mins < 1) return 'Just now'
  if (mins < MINUTES_PER_HOUR) return `${mins}m ago`
  const hrs = Math.floor(mins / MINUTES_PER_HOUR)
  if (hrs < HOURS_PER_DAY) return `${hrs}h ago`
  const days = Math.floor(hrs / HOURS_PER_DAY)
  return `${days}d ago`
}

const ariaLabel = computed(() => {
  const status = props.notification.isRead ? 'read' : 'unread'
  return `${props.notification.title}, ${relativeTime(props.notification.createdAt)}, ${status}`
})

const absoluteTime = computed(() => formatTimestamp(props.notification.createdAt))
</script>

<template>
  <Box
    is="button"
    type="button"
    :aria-label="ariaLabel"
    class="notif-item"
    :class="{ 'notif-item--unread': !notification.isRead }"
    align="center"
    gap="3"
    @click="emit('click', notification)"
  >
    <IconBox
      :icon="typeStyle[notification.type].icon"
      size="xl"
      :bg="typeStyle[notification.type].bg"
      :icon-color="typeStyle[notification.type].iconColor"
    />

    <Box direction="col" gap="1" grow class="notif-item__content">
      <Text
        type="body"
        :weight="notification.isRead ? 'medium' : 'semibold'"
        :truncate="2"
      >
        {{ notification.title }}
      </Text>
      <Text type="body-sm" color="muted" :truncate="2">
        {{ notification.body }}
      </Text>
      <Text
        type="caption"
        color="muted"
        is="span"
        :title="absoluteTime"
        class="notif-item__time"
      >
        {{ relativeTime(notification.createdAt) }}
      </Text>
    </Box>

    <Box v-if="!notification.isRead" class="notif-item__dot" aria-hidden="true" />
  </Box>
</template>

<style scoped>
.notif-item {
  width: 100%;
  padding: 0.875rem 1.25rem;
  background: var(--p-surface-0);
  border: none;
  border-top: 1px solid var(--p-surface-100);
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}

.notif-item:hover {
  background: var(--p-surface-50);
}

.notif-item--unread {
  background: var(--p-primary-50);
}

.notif-item--unread:hover {
  background: var(--p-primary-100);
}

.notif-item__content {
  min-width: 0;
}

.notif-item__time {
  margin-top: 2px;
}

.notif-item__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--p-primary-500);
  flex-shrink: 0;
}
</style>
